import React, { useEffect, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { DogWalk, NormalizedDogWalk } from '../types';

const dogWalkIcon = new L.Icon({
  iconUrl: '/green-paw-marker.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

interface DogWalkSearchLayerProps {
  location: { lat: number; lng: number };
  radius?: number;
  onWalkSelect?: (walk: NormalizedDogWalk) => void;
}

const DogWalkSearchLayer: React.FC<DogWalkSearchLayerProps> = ({ location, radius = 25, onWalkSelect }) => {
  const [walks, setWalks] = useState<DogWalk[]>([]);

  // Fetch walks when location changes
  useEffect(() => {
    const fetchWalks = async () => {
      const formData = new URLSearchParams();
      formData.append('lat', location.lat.toString());
      formData.append('lon', location.lng.toString());
      formData.append('radius', radius.toString());

      try {
        const response = await fetch('/api/dog-walks-search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lat: location.lat, lon: location.lng, radius }),
        });

        if (!response.ok) {
          console.warn('Dog walk fetch failed:', response.status);
          setWalks([]);
          return;
        }

        const data = await response.json();
        // console.log('Dog walk API response sample:', data.walks?.[0]);
        if (Array.isArray(data.walks)) {
          setWalks(data.walks);
        } else {
          console.warn('Unexpected response format:', data);
          setWalks([]);
        }
      } catch (error) {
        console.error('Error fetching dog walks:', error);
        setWalks([]);
      }
    };

    fetchWalks();
  }, [location.lat, location.lng, radius]);

  return (
    <>
      {walks.map(walk => {
        const lat = parseFloat(walk.latitude);
        const lng = parseFloat(walk.longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        return (
          <Marker
            key={walk.id}
            position={[lat, lng]}
            icon={dogWalkIcon}
            eventHandlers={{
              click: () => {
                if (onWalkSelect) {
                  const normalized: NormalizedDogWalk = {
                    id: walk.id,
                    name: walk.name,
                    type: 'dogwalk',
                    location: { lat, lng },
                    dogFriendly: true,
                    source: 'dogwalksnearme',
                    description: walk.description_html || walk.location_name,
                    featured_image_path: walk.featured_image_path
                  };
                  onWalkSelect(normalized);
                }
              }
            }}
          >
            <Popup>
              <strong>{walk.name}</strong><br />
              {walk.location_name}<br />
              {walk.distance_miles} miles<br />
              <a href={`https://www.dogwalksnearme.co.uk/walks/${walk.slug}`} target="_blank" rel="noreferrer">View Walk</a>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default DogWalkSearchLayer;